import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, X, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "./ui/dialog";
import { PRACTICE_AREAS, FIRM } from "../lib/practiceAreas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLOTS = [
  "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00",
  "14:30", "15:00", "15:30", "16:00",
];

const fmtDate = (d) => d.toISOString().slice(0, 10);
const longDate = (s) => {
  if (!s) return "";
  const [y, m, day] = s.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
};

// Generate next ~3 weeks of weekdays (Mon-Fri only), skipping today if past 4pm
const buildDateOptions = () => {
  const now = new Date();
  const cutoffPast4pm = now.getHours() >= 16;
  const out = [];
  let cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (cutoffPast4pm) cursor.setDate(cursor.getDate() + 1);
  let added = 0;
  while (added < 15) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) {
      out.push(new Date(cursor));
      added += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

export const BookingDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1=date/time, 2=details, 3=success
  const dates = useMemo(() => buildDateOptions(), []);
  const [selectedDate, setSelectedDate] = useState(fmtDate(dates[0]));
  const [selectedTime, setSelectedTime] = useState("");
  const [availability, setAvailability] = useState({});
  const [loadingAv, setLoadingAv] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", practice_area: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch availability when date changes
  useEffect(() => {
    if (!open || !selectedDate) return;
    let cancelled = false;
    setLoadingAv(true);
    setSelectedTime("");
    axios
      .get(`${API}/bookings/availability`, { params: { date: selectedDate } })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data?.slots || []).forEach((s) => { map[s.time] = s.booked; });
        setAvailability(map);
      })
      .catch(() => {
        if (!cancelled) setAvailability({});
      })
      .finally(() => { if (!cancelled) setLoadingAv(false); });
    return () => { cancelled = true; };
  }, [open, selectedDate]);

  const reset = () => {
    setStep(1);
    setSelectedDate(fmtDate(dates[0]));
    setSelectedTime("");
    setAvailability({});
    setForm({ name: "", email: "", phone: "", practice_area: "", notes: "" });
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 200);
  };

  const onContinue = () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please pick a date and an available time.");
      return;
    }
    setStep(2);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can confirm your slot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        phone: form.phone || null,
        practice_area: form.practice_area || null,
        notes: form.notes || null,
        date: selectedDate,
        time: selectedTime,
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Walk-in slot booked. Confirmation on its way.");
      setStep(3);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(typeof detail === "string" ? detail : "That slot was just booked. Please choose another.");
        setStep(1);
        // refresh availability
        try {
          const res = await axios.get(`${API}/bookings/availability`, { params: { date: selectedDate } });
          const map = {};
          (res.data?.slots || []).forEach((s) => { map[s.time] = s.booked; });
          setAvailability(map);
        } catch { /* ignore */ }
      } else if (err?.response?.status === 429) {
        toast.error(typeof detail === "string" ? detail : "This email already has a recent booking. Please wait 7 days or call us.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't book your slot. Please try again or call us.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0B0B0E] border border-white/10 text-[#F5F2E8] max-w-4xl rounded-none p-0 max-h-[92vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">Book a free walk-in slot at Lawfields Solicitors</DialogTitle>
        <DialogDescription className="sr-only">
          Select an available date and time for your 20-minute free walk-in consultation, then provide your contact details.
        </DialogDescription>
        <div className="grid md:grid-cols-12 min-h-[500px]">
          {/* Sidebar */}
          <aside className="hidden md:flex md:col-span-4 flex-col justify-between bg-[#14141A] p-8 border-r border-white/5">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#E9C76A]" />
                <span className="overline">Complimentary</span>
              </div>
              <h3 className="font-serif text-3xl leading-tight tracking-tight">
                Free walk-in slot
              </h3>
              <p className="mt-4 text-sm text-white/60 font-light leading-relaxed">
                A 20-minute confidential consultation with a partner — no
                obligation, no cost.
              </p>
              <ul className="mt-8 space-y-4 text-sm text-white/70 font-light">
                <li className="flex items-start gap-3">
                  <Clock size={18} weight="thin" className="text-[#E9C76A] mt-0.5 flex-shrink-0" />
                  Mon – Fri · 9:30am – 4:30pm
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} weight="thin" className="text-[#E9C76A] mt-0.5 flex-shrink-0" />
                  {FIRM.address}
                </li>
              </ul>
            </div>
            <div className="text-[10px] text-white/30 tracking-widest uppercase mt-10">
              SRA Regulated · No. {FIRM.sra}
            </div>
          </aside>

          {/* Main */}
          <div className="md:col-span-8 p-7 md:p-10 relative">
            <button
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-white/40 hover:text-[#E9C76A] transition-colors z-10"
              data-testid="booking-close"
            >
              <X size={20} weight="thin" />
            </button>

            {/* Stepper */}
            <div className="flex items-center gap-3 mb-8">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div
                    className={`h-7 w-7 rounded-full border flex items-center justify-center text-[11px] font-medium ${
                      step >= n
                        ? "bg-[#E9C76A] text-[#0F0F0F] border-[#E9C76A]"
                        : "border-white/15 text-white/40"
                    }`}
                  >
                    {step > n ? "✓" : n}
                  </div>
                  {n < 3 && (
                    <div className={`flex-1 h-px ${step > n ? "bg-[#E9C76A]" : "bg-white/10"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h4 className="font-serif text-2xl mb-1">Choose a date & time</h4>
                <p className="text-sm text-white/55 mb-6">Walk-in slots are 20 minutes each.</p>

                <div className="mb-6">
                  <div className="overline mb-3 !text-white/40">Available dates</div>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                    {dates.map((d) => {
                      const v = fmtDate(d);
                      const isActive = v === selectedDate;
                      return (
                        <button
                          key={v}
                          data-testid={`booking-date-${v}`}
                          onClick={() => setSelectedDate(v)}
                          className={`flex flex-col items-center flex-shrink-0 w-16 py-3 border transition-colors ${
                            isActive
                              ? "border-[#E9C76A] bg-[#E9C76A]/10 text-[#F5F2E8]"
                              : "border-white/10 text-white/55 hover:border-white/30"
                          }`}
                        >
                          <span className="text-[10px] uppercase tracking-widest">
                            {d.toLocaleDateString("en-GB", { weekday: "short" })}
                          </span>
                          <span className="font-serif text-xl mt-1">{d.getDate()}</span>
                          <span className="text-[10px] uppercase text-white/40">
                            {d.toLocaleDateString("en-GB", { month: "short" })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="overline mb-3 !text-white/40">
                    {longDate(selectedDate)}{" "}
                    {loadingAv && <span className="text-white/30 ml-2 normal-case tracking-normal text-[11px]">loading...</span>}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {SLOTS.map((t) => {
                      const booked = !!availability[t];
                      const isActive = t === selectedTime;
                      return (
                        <button
                          key={t}
                          data-testid={`booking-slot-${t}`}
                          disabled={booked || loadingAv}
                          onClick={() => setSelectedTime(t)}
                          className={`py-3 text-sm font-sans border transition-colors ${
                            isActive
                              ? "bg-[#E9C76A] text-[#0F0F0F] border-[#E9C76A]"
                              : booked
                              ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                              : "border-white/10 text-white/75 hover:border-[#E9C76A]/60 hover:text-[#E9C76A]"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    data-testid="booking-continue"
                    onClick={onContinue}
                    disabled={!selectedTime}
                    className="btn-gold inline-flex items-center gap-3"
                  >
                    Continue <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.form
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={onSubmit}
                noValidate
              >
                <h4 className="font-serif text-2xl mb-1">Your details</h4>
                <p className="text-sm text-white/55 mb-6">
                  Booking for{" "}
                  <span className="text-[#E9C76A]">{longDate(selectedDate)}</span> at{" "}
                  <span className="text-[#E9C76A]">{selectedTime}</span>.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="overline mb-2 block !text-white/40">Full Name</label>
                    <input
                      data-testid="booking-input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="lawfields-input w-full"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block !text-white/40">Email</label>
                    <input
                      data-testid="booking-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="lawfields-input w-full"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block !text-white/40">Phone (optional)</label>
                    <input
                      data-testid="booking-input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="lawfields-input w-full"
                      placeholder="+44 7XXX XXXXXX"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block !text-white/40">Practice Area (optional)</label>
                    <select
                      data-testid="booking-input-area"
                      value={form.practice_area}
                      onChange={(e) => setForm({ ...form, practice_area: e.target.value })}
                      className="lawfields-input w-full appearance-none"
                    >
                      <option value="">Select an area</option>
                      {PRACTICE_AREAS.map((a) => (
                        <option key={a.id} value={a.title}>{a.title}</option>
                      ))}
                      <option value="General Enquiry">General Enquiry</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="overline mb-2 block !text-white/40">A brief note (optional)</label>
                  <textarea
                    data-testid="booking-input-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="lawfields-input w-full resize-none"
                    placeholder="Anything we should know in advance..."
                  />
                </div>

                <div className="mt-7 flex flex-col sm:flex-row justify-between gap-4">
                  <button
                    type="button"
                    data-testid="booking-back"
                    onClick={() => setStep(1)}
                    className="text-xs uppercase tracking-[0.2em] text-white/55 hover:text-[#E9C76A] transition-colors"
                  >
                    ← Change date/time
                  </button>
                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={submitting}
                    className="btn-gold inline-flex items-center gap-3"
                  >
                    {submitting ? "Confirming..." : "Confirm Booking"}
                    <Calendar size={14} weight="bold" />
                  </button>
                </div>
              </motion.form>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="py-8 text-center"
                data-testid="booking-success"
              >
                <CheckCircle size={56} weight="thin" className="mx-auto text-[#E9C76A] mb-4" />
                <h4 className="font-serif text-3xl mb-3">You&apos;re booked.</h4>
                <p className="text-white/65 max-w-md mx-auto font-light leading-relaxed">
                  We&apos;ll see you{" "}
                  <span className="text-[#E9C76A]">{longDate(selectedDate)}</span> at{" "}
                  <span className="text-[#E9C76A]">{selectedTime}</span>. A confirmation
                  has been sent to <span className="text-[#F5F2E8]">{form.email}</span>.
                </p>
                <div className="mt-8 border border-white/10 bg-[#14141A] inline-block text-left p-5 text-sm">
                  <div className="overline mb-2">Where to find us</div>
                  <div className="text-white/80">{FIRM.address}</div>
                  <div className="text-white/55 mt-1">{FIRM.phone}</div>
                </div>
                <div className="mt-8">
                  <button
                    data-testid="booking-done"
                    onClick={() => setOpen(false)}
                    className="text-xs uppercase tracking-[0.2em] text-[#E9C76A] hover:underline underline-offset-4"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
